/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import TWEEN from '@tweenjs/tween.js'
import { Region, TokenType } from '@vatom/sdk/core'

import Alert from '../../components/Alert'

import TokenObject from './TokenObject'
import { ARViewerWeb } from '.'

declare let THREE: any

// Reusable vars
const v1 = new THREE.Vector3()
const yAxis = new THREE.Vector3(0, 1, 0)

/** The distance from the camera that AR objects are placed */
const DistanceFromCamera = 5

/** The starting height, on the Y axis */
const ObjectBaseHeight = 2

export interface ARConfig {
  tokenID: string
  visibleRadius: number
  distanceMultiplier: number
  minDistance: number
  fqdnWhitelist: string
  maxItems: number
  closeOnPickup: boolean
  errorIfEmpty: boolean
  showRecord: boolean
  resizeObjects: boolean
  rearrangeObjects: boolean
  desiredRadius: number
  scalingStrategy: 'new' | 'old'
  distanceFromCamera: number
  objectBaseHeight: number
  minimumDistance: number
  additionalRandomDistance: number
  ambientLightIntensity: number
  maxPoints: number
  userPoints: number
}

export type Geolocation = {
  altitude?: number | null
  latitude: number
  longitude: number
}

/** Responsible for loading and displaying tokens in the 3D environment */
export default class TokenPlugin {
  ar: ARViewerWeb
  region?: Region
  tokens: TokenObject[]
  // VisibleRadius: any
  // distanceMultiplier: any
  // minimumDistance = 1;
  // additionalRandomDistance = 5;
  actionCalled: boolean
  numLoading: number
  v1: any
  loadPromiseResolver: any
  loadPromiseFailer: ((reason?: any) => void) | null

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar
    this.ar.tokenPlugin = this

    // Currently displayed 3D tokens
    this.tokens = []

    // The meter distance around the user in which Tokens will be visible
    // this.VisibleRadius = Config.features?.ar?.visible_radius || 500

    // Should we move objects closer to / farther from the camera, and what distance multiplier should we use? 1 = off
    // this.distanceMultiplier = Config.features?.ar?.distance_multiplier || 0.05

    // If a token is too close, it will be moved this far away from the user (in meters)
    // if (this.config.minDistance) this.minimumDistance = this.config.minDistance;

    this.actionCalled = false

    // Number of loading objects
    this.numLoading = 0

    // Vars used for doing calculations
    this.v1 = new THREE.Vector3()

    // Start updating tween lib. This must only happen once, even if multiple components use the Tween library.
    if (!(window as any).tweenIsRunning) {
      ;(window as any).tweenIsRunning = true
      const animate = function (time: number) {
        requestAnimationFrame(animate)
        TWEEN.update(time)
      }
      requestAnimationFrame(animate)
    }

    this.loadPromiseFailer = (err: any) => {
      throw new Error('Method not implemented.')
    }
  }

  get name() {
    return 'TokenPlugin'
  }

  get config(): ARConfig {
    return {
      maxItems: 5,
      resizeObjects: true,
      rearrangeObjects: true,
      desiredRadius: 1,
      scalingStrategy: 'new',
      distanceFromCamera: 5,
      objectBaseHeight: 2,
      minimumDistance: 1,
      additionalRandomDistance: 5,
      //...Config.features?.ar,
      ...this.ar.customSession?.config
    }
  }

  async load(div: HTMLDivElement) {
    // Stop if this is a custom session
    if (this.ar.customSession?.config.modelUrl) return

    // Load started
    console.debug(
      `[AR] TokenPlugin loading: maxItems=${this.config.maxItems || 2} minDistance=${
        this.config.minimumDistance
      } additionalRandomDistance=${this.config.additionalRandomDistance}`
    )

    // Remove old region if needed
    // if (this.region) this.region.close();

    // Check if we've been given a list of token IDs to use, or if we should fetch tokens from the map
    // if (this.ar.props.vatomIDs?.length === 0) {

    // 	// Show status
    // 	this.ar.setState({ loadingText: "Fetching nearby tokens..." });

    // 	// Create a coordinate square around our current position
    // 	let coords = this.geolocation;
    // 	let regionBounds : any = coordinatesToBounds(coords.latitude, coords.longitude, this.config.visibleRadius || 500)

    // 	// Add a filter for FQDN if any exists
    // 	if (this.config.fqdnWhitelist)
    // 		regionBounds.publisher_fqdn = this.config.fqdnWhitelist;

    // 	// Create new region
    // 	this.region = this.ar.props.sdk.dataPool.region("geopos", JSON.stringify(regionBounds))
    // 	console.log("Created new region with bounds: ", regionBounds, this.region)
    // 	// console.info(
    // 	// 	"Created new region with bounds: ",
    // 	// 	regionBounds,
    // 	// 	this.region
    // 	// );

    // } else {

    // 	// Show status
    // 	this.ar.setState({ loadingText: "Fetching tokens..." })

    // 	// We have specific token IDs, load a region with just those
    // 	this.region = this.ar.props.sdk.dataPool.region("ids", this.ar.props.vatomIDs?.join(','))
    // 	console.log("Created new region with IDs: ", this.ar.props.vatomIDs, this.region)

    // }

    // Add region listener
    // console.info("Adding listener");
    // this.region!.on("updated", this.tokensUpdated);

    // Listen for region error
    // this.region!.on("error", this.onError);

    // If region already has some content, refresh immediately
    // const objects = this.region?.get(false as any) as any as TokenType[]
    // if (objects?.length > 0) this.tokensUpdated()

    // Create a promise which we will resolve once all tokens have loaded
    // await new Promise((resolve, reject) => {
    // 	this.loadPromiseResolver = resolve;
    // 	this.loadPromiseFailer = reject;
    // })
  }

  /** Get geolocation */
  get geolocation(): Geolocation {
    // Check if loaded by Geolocation plugin
    if (this.ar.currentGeolocation) return this.ar.currentGeolocation

    // Nope, check if our region has a token we can use as a reference
    const tokens = (this.region?.get(false as any) || []) as TokenType[]
    if (tokens.length !== 0)
      return {
        altitude: 0,
        latitude: tokens[0].position?.coordinates[1] ?? 0,
        longitude: tokens[0].position?.coordinates[0] ?? 0
      }

    // Unknown location!
    return {
      altitude: 0,
      latitude: 0,
      longitude: 0
    }
  }

  isValidToken(token: TokenType) {
    // const studioInfo = normalizeStudioInfo(token);
    // if (this.ar.customSession?.config.filter) {
    // 	let objectDefinition = getIdFromIdOrUri(
    // 		this.ar.customSession?.config.filter
    // 	);

    // 	return studioInfo?.objectDefinitionId === objectDefinition;
    // }
    // if (this.ar.props.business) {
    // 	return studioInfo?.businessId === this.ar.props.business.id
    // }
    return true
  }

  positionObject(existingTokenObject: TokenObject) {
    // Meter to coordinate calculations
    const latToMeters = 111111
    const lonToMeters = 111111 * Math.cos(this.geolocation.latitude)

    // Move to appropriate position
    const newPosition = v1
    // let distanceMultiplier = this.config.distanceMultiplier || 0.05

    // Calculate random height
    const randomHeightFrom = 1
    const randomHeightTo = 2
    existingTokenObject.randomHeight =
      existingTokenObject.randomHeight ||
      Math.random() * (randomHeightTo - randomHeightFrom) + randomHeightFrom
    // let tokenAltitude = existingTokenObject.token.altitude || existingTokenObject.randomAltitude

    if (existingTokenObject.token.position) {
      newPosition.x =
        (this.geolocation.latitude - existingTokenObject.token.position.coordinates[1] || 0) *
        latToMeters
      newPosition.y = 0 //(tokenAltitude - this.geolocation.altitude)
      newPosition.z =
        (this.geolocation.longitude - existingTokenObject.token.position.coordinates[0] || 0) *
        lonToMeters
    }
    // Randomize the distance
    existingTokenObject.appliedDistance =
      existingTokenObject.appliedDistance ||
      this.config.minimumDistance + Math.random() * this.config.additionalRandomDistance

    // If position was exactly on top of the camera, ie the coordinates match exactly with our current position,
    // move it away to the minimum distance
    if (newPosition.x === 0 && newPosition.z === 0) newPosition.z = 1

    // Make sure token is the exact distance away from the user
    // let nY = newPosition.y
    newPosition.y = 0
    newPosition.normalize()
    if (this.config.scalingStrategy === 'new') {
      console.info(
        'Scaling: ',
        existingTokenObject.appliedDistance,
        existingTokenObject.randomHeight
      )
      newPosition.multiplyScalar(existingTokenObject.appliedDistance)
      newPosition.y = existingTokenObject.randomHeight //Math.max(0, Math.min(2, nY)) || 0
    } else {
      console.info('Scaling: ', this.config.distanceFromCamera, this.config.objectBaseHeight)
      newPosition.multiplyScalar(this.config.distanceFromCamera)
      newPosition.y = this.config.objectBaseHeight
    }

    // Stop if the new position is the same as the last position
    if (
      newPosition.x === existingTokenObject.lastX &&
      newPosition.y === existingTokenObject.lastY &&
      newPosition.z === existingTokenObject.lastZ
    ) {
      // No change, stop
      return console.log('[AR] Skipping object update, no changes')
    } else {
      // Has changed, store position and continue
      existingTokenObject.lastX = newPosition.x
      existingTokenObject.lastY = newPosition.y
      existingTokenObject.lastZ = newPosition.z
    }

    // Check if token has been moved into it's position yet
    if (existingTokenObject.hasSetPosition) {
      // Stop if the object hasn't moved
      if (existingTokenObject.container.position.equals(newPosition))
        return console.log('[AR] Skipping object update, no change')

      // Make object look in the direction it's moving
      existingTokenObject.container.lookAt(newPosition)

      // Skip if an extremely recent tween was started
      if ((existingTokenObject.currentTweenTime || 0) + 100 > Date.now())
        return console.log('[AR] Skipping object update, too soon')

      // Stop existing tween animation
      if (existingTokenObject.currentTween) existingTokenObject.currentTween.stop()

      // Animate to next position. Create tween
      existingTokenObject.currentTweenTime = Date.now()
      existingTokenObject.currentTween = new TWEEN.Tween(existingTokenObject.container.position)
        .to({ x: newPosition.x, y: newPosition.y, z: newPosition.z }, 750)
        .start()
      // existingTokenObject.container.position.copy(newPosition)
      console.log('[AR] Updating position with tween:', newPosition)
    } else {
      // If object is too close to another object, move it up
      const objectSizeRadius = 0.95
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Go in a circle until we can find a spot for this token (divide full radius into 40 segments)
        let found = false
        const segments = 40

        // Randomly pick a starting point
        newPosition.applyAxisAngle(yAxis, Math.PI * 2 * Math.random())

        for (let i = 0; i < segments; i++) {
          // Check if there's another object within our radius
          const objectWithinRadius = this.tokens.find(
            obj =>
              obj !== existingTokenObject &&
              obj.container.position.distanceTo(newPosition) < objectSizeRadius
          )
          if (!objectWithinRadius) {
            // We can use this position, stop here
            found = true
            break
          }

          // Rotate it a bit
          newPosition.applyAxisAngle(yAxis, (Math.PI * 2) / segments)
        }

        // Stop if a position has been found
        if (found) break

        // There is an object here, move further up and try again
        newPosition.y += 0.5
      }

      // Set position immediately
      existingTokenObject.container.position.copy(newPosition)
      existingTokenObject.hasSetPosition = true
      console.log('[AR] Setting position:', existingTokenObject.container.position)
    }
  }

  /** Called when the main AR component is updated */
  componentDidUpdate() {
    // Update tokens
    this.tokensUpdated()
  }

  /** Called when the tokens in our region have changed */
  tokensUpdated = () => {
    console.info('[TokenPlugin] In tokensUpdated')

    // Get new list of tokens
    const currentTokens = this.ar.props.tokens || []

    // Remove missing ones
    for (let i = 0; i < this.tokens.length; i++) {
      // Get existing token
      const existingTokenObject: any = this.tokens[i]

      // Check if it still exists
      const newToken = currentTokens.find(t => t.id == existingTokenObject.token.id)
      if (newToken && newToken.position && this.isValidToken(newToken)) continue

      // No longer in array or not dropped, remove it!
      this.ar.scene.remove(existingTokenObject.container)
      this.tokens.splice(i--, 1)
      console.log(
        `[AR] Removing token ${existingTokenObject.token.properties.title} - ${existingTokenObject.token.id}`
      )
    }

    // Add/update tokens
    let numberFound = 0
    const maxItems = this.config.maxItems || 2
    for (const newToken of currentTokens) {
      // TODO Check if allowed to display this token
      if (!this.isValidToken(newToken)) return

      // Find existing token
      numberFound++
      let existingTokenObject = this.tokens.find(to => to.token.id === newToken.id)
      if (!existingTokenObject) {
        // Not found, create it
        // console.log('[AR] Adding token ' + newToken.properties.title + ' - ' + newToken.id)

        // Stop if we have too many tokens on-screen already
        if (this.tokens.length >= maxItems) {
          // console.log('[AR] Max # of tokens reached')
          return
        }

        existingTokenObject = new TokenObject(
          newToken,
          this.ar.scene,
          this.ar.renderer,
          this.ar.audioListener,
          this.config
        )
        this.tokens.push(existingTokenObject)
        this.ar.scene.add(existingTokenObject.container)

        // Load it
        this.numLoading += 1
        this.updateLoadingStatus()
        existingTokenObject
          .load()
          .catch(err => {
            // Failed to load!
            console.warn('Failed to load Token in AR view!', err)
          })
          .then(e => {
            // Update loading count
            this.numLoading -= 1
            this.updateLoadingStatus()
          })

        // Add click handler
        existingTokenObject.onClick = this.onTokenClick.bind(this, existingTokenObject)

        // Add action handler
        existingTokenObject.tokenActionPerformedListener = (e: any) => {
          // Notify all plugins
          for (const plugin of this.ar.plugins) if (plugin.onTokenAction) plugin.onTokenAction(e)
        }
      }

      // Update token reference, just in case
      existingTokenObject.token = newToken

      // Check if we should rearrange objects
      if (this.config.rearrangeObjects) {
        this.positionObject(existingTokenObject)
      }
    }

    // if (this.region?.synchronized && !!this.ar.customSession?.tokensUpdated) {
    // 	this.ar.customSession.tokensUpdated({
    // 		geolocation: {
    // 			longitude: this.geolocation.longitude,
    // 			latitude: this.geolocation.latitude,
    // 		},
    // 		numberFound,
    // 	});
    // }

    // const showError = this.config.errorIfEmpty === undefined ? true : this.config.errorIfEmpty
    // const showError = this.ar.customSession ? false : true; // temp

    // // If no tokens nearby, fail
    // console.info(
    // 	"Checking if none",
    // 	this.loadPromiseResolver,
    // 	this.region?.synchronized,
    // 	numberFound,
    // 	this.loadPromiseFailer,
    // 	showError
    // );
    // if (
    // 	this.loadPromiseResolver &&
    // 	this.region?.synchronized &&
    // 	numberFound === 0
    // ) {
    // 	if (this.loadPromiseFailer) {
    // 		if (showError)
    // 			this.loadPromiseFailer(
    // 				new Error("There are no tokens near you on the map.")
    // 			);
    // 		else this.loadPromiseResolver();
    // 	}

    // 	this.loadPromiseResolver = null;
    // 	this.loadPromiseFailer = null;
    // }
  }

  onError = (err: any) => {
    // Check if load promise is complete already
    if (!this.loadPromiseResolver) return

    // Fail loading
    if (this.loadPromiseFailer) this.loadPromiseFailer(err)

    this.loadPromiseFailer = null
    this.loadPromiseResolver = null

    // If unauthorized, go back to login screen
    // if (err.code === "UNAUTHORIZED") this.ar.props.history.push("/");
  }

  unload() {
    if (this.region) {
      this.region.off('updated', this.tokensUpdated)
      this.region.off('error', this.onError)
      // this.region.close();
    }
  }

  /** Show debug stats */
  debugStats() {
    return `Tokens: ${this.tokens.length} ${
      this.numLoading > 0 ? ` (Loading ${this.numLoading})` : ''
    }`
  }

  /** Update the loading status */
  updateLoadingStatus() {
    // Only do if we've got a pending load promise
    if (!this.loadPromiseResolver) return

    // Check number of loading items
    this.ar.setState({
      loadingText: `Loading ${this.numLoading} tokens...`,
      loadingProgress: (this.tokens.length - this.numLoading) / this.tokens.length
    })

    // Check if complete
    if (this.numLoading === 0) {
      // Resolve the loading promise
      this.loadPromiseResolver()
      this.loadPromiseResolver = null
      this.loadPromiseFailer = null
    }
  }

  /** Called when the user clicks on a token object */
  async onTokenClick(tokenObject: TokenObject) {
    // Pass on to the host component
    this.ar.props.onTokenSelect?.(tokenObject.token)

    // const { token } = tokenObject;
    // let actionName;

    // Check if custom action requested
    // if (this.ar.config.action) {
    // 	actionName = this.ar.options.action;

    // 	// Show loader
    // 	// Alert.quickFire(this.ar.options.actionMessage || "Picking up token...");
    // 	// Catch errors
    // 	try {
    // 		// Perform action
    // 		token.performAction(actionName);

    // 		// Log analytics
    // 		// Analytics.event(
    // 		// 	"performAction",
    // 		// 	{
    // 		// 		event: "performAction",
    // 		// 		actionUri: actionName,
    // 		// 		eventValue: 1,
    // 		// 	},
    // 		// 	token
    // 		// );
    // 	} catch (err) {
    // 		// Show error
    // 		// Errors.show(err);
    // 	}

    // 	// Dismiss loader
    // 	// Alert.close();
    // 	this.ar.props.history.push("/");
    // } else {
    // 	const isDispenser = token.actions.includes("Dispense");

    // 	if (isDispenser)
    // 		actionName = await handleDispense(token, this.ar.props.history);
    // 	else
    // 		actionName = await handlePickup(
    // 			token,
    // 			this.ar.props.history,
    // 			this.geolocation,
    // 			this.config.closeOnPickup === false
    // 		);
    // // }

    // if (tokenObject.tokenActionPerformedListener && actionName)
    // 	tokenObject.tokenActionPerformedListener({
    // 		token,
    // 		actionName,
    // 		actionData: {},
    // 	});
  }

  /** Called every frame */
  onRender3D(delta: any, scene: any, camera: any) {
    // Notify objects
    for (const obj of this.tokens) obj.onRender3D(delta, scene, camera)
  }
}

/**
 * Converts a latitiude, longitude and radius to a region, ie. bottom-left and top-right.
 *
 * Adapted from https://github.com/mapbox/mapbox-gl-js/blob/2fe94c362b1f019f7e878c1d80b36e544d7c241e/src/geo/lng_lat.js#L85
 */
function coordinatesToBounds(latitude: number, longitude: number, radius: number) {
  const earthCircumferenceInMetersAtEquator = 40075017
  const latAccuracy = (360 * radius) / earthCircumferenceInMetersAtEquator
  const lngAccuracy = latAccuracy / Math.cos((Math.PI / 180) * latitude)

  return {
    bottom_left: {
      lon: longitude - lngAccuracy,
      lat: latitude - latAccuracy
    },
    top_right: {
      lon: longitude + lngAccuracy,
      lat: latitude + latAccuracy
    }
  }
}
